// src/pages/AcceptInvitePage.jsx
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { organizationApi } from '../api/organizationApi';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const PASSWORD_RULES = [
    { id: 'length', label: '8+ caratteri', test: p => p.length >= 8 },
    { id: 'upper', label: 'Maiuscola', test: p => /[A-Z]/.test(p) },
    { id: 'lower', label: 'Minuscola', test: p => /[a-z]/.test(p) },
    { id: 'number', label: 'Numero', test: p => /[0-9]/.test(p) },
];

const AcceptInvitePage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', password: '', confirmPassword: ''
    });
    const [errors, setErrors] = useState({});

    const passwordScore = PASSWORD_RULES.filter(r => r.test(formData.password)).length;
    const strengthColors = ['bg-red-500', 'bg-orange-400', 'bg-yellow-400', 'bg-emerald-500'];

    const validate = () => {
        const e = {};
        if (!formData.firstName.trim()) e.firstName = 'Nome richiesto';
        if (!formData.lastName.trim()) e.lastName = 'Cognome richiesto';
        if (!PASSWORD_RULES.every(r => r.test(formData.password))) e.password = 'Password non sicura';
        if (formData.password !== formData.confirmPassword) e.confirmPassword = 'Le password non coincidono';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        setError('');
        try {
            const response = await organizationApi.acceptInvitation(token, {
                firstName: formData.firstName.trim(),
                lastName:  formData.lastName.trim(),
                password:  formData.password,
            });
            if (response.success) {
                // ✅ Chiavi corrette — stesse usate da AuthContext e axiosConfig
                localStorage.setItem('roadctrl_token',   response.data.accessToken || response.data.token);
                localStorage.setItem('roadctrl_refresh',  response.data.refreshToken || '');
                localStorage.setItem('roadctrl_user',     JSON.stringify(response.data.user));
                toast.success('Account creato! Benvenuto in InfraPilot 🎉');
                // Piccolo delay per permettere al context di caricarsi
                setTimeout(() => navigate('/dashboard'), 300);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Errore durante la registrazione');
        } finally {
            setLoading(false);
        }
    };

    const inputClass = (field) =>
        `w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-gray-50 ${errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-200'
        }`;

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12">
            <div className="max-w-md w-full mx-4">

                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4 shadow-lg">
                        <span className="text-2xl font-bold text-white">iP</span>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">InfraPilot</h1>
                    <p className="text-gray-500 mt-1 text-sm">Construction Site Management</p>
                </div>

                <div className="bg-white rounded-2xl shadow-xl border overflow-hidden">
                    {/* Header card */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 text-white">
                        <h2 className="text-lg font-bold">Sei stato invitato! 🎉</h2>
                        <p className="text-blue-200 text-sm mt-0.5">Completa la registrazione per unirti al team</p>
                    </div>

                    <div className="p-6">
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                                <span>⚠️</span> {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4" noValidate>

                            {/* Nome + Cognome */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome *</label>
                                    <input
                                        type="text"
                                        autoComplete="given-name"
                                        value={formData.firstName}
                                        onChange={e => { setFormData({ ...formData, firstName: e.target.value }); setErrors({ ...errors, firstName: '' }); }}
                                        className={inputClass('firstName')}
                                        placeholder="Mario"
                                    />
                                    {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Cognome *</label>
                                    <input
                                        type="text"
                                        autoComplete="family-name"
                                        value={formData.lastName}
                                        onChange={e => { setFormData({ ...formData, lastName: e.target.value }); setErrors({ ...errors, lastName: '' }); }}
                                        className={inputClass('lastName')}
                                        placeholder="Rossi"
                                    />
                                    {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                                </div>
                            </div>

                            {/* Password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password *</label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        value={formData.password}
                                        onChange={e => { setFormData({ ...formData, password: e.target.value }); setErrors({ ...errors, password: '' }); }}
                                        className={`${inputClass('password')} pr-10`}
                                        placeholder="••••••••"
                                    />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                                        {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                                    </button>
                                </div>

                                {formData.password && (
                                    <>
                                        <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full transition-all ${strengthColors[passwordScore - 1] || 'bg-gray-200'}`}
                                                style={{ width: `${(passwordScore / 4) * 100}%` }}
                                            />
                                        </div>
                                        <div className="mt-2 grid grid-cols-2 gap-1">
                                            {PASSWORD_RULES.map(rule => {
                                                const ok = rule.test(formData.password);
                                                return (
                                                    <div key={rule.id} className="flex items-center gap-1">
                                                        {ok
                                                            ? <CheckCircleIcon className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                                                            : <XCircleIcon className="w-3 h-3 text-gray-300 flex-shrink-0" />
                                                        }
                                                        <span className={`text-xs ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>{rule.label}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                            </div>

                            {/* Conferma password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Conferma Password *</label>
                                <input
                                    type="password"
                                    autoComplete="new-password"
                                    value={formData.confirmPassword}
                                    onChange={e => { setFormData({ ...formData, confirmPassword: e.target.value }); setErrors({ ...errors, confirmPassword: '' }); }}
                                    className={`${inputClass('confirmPassword')} ${formData.confirmPassword && formData.password === formData.confirmPassword
                                            ? '!border-emerald-400 !bg-emerald-50'
                                            : ''
                                        }`}
                                    placeholder="••••••••"
                                />
                                {formData.confirmPassword && formData.password === formData.confirmPassword && (
                                    <p className="text-emerald-600 text-xs mt-1 flex items-center gap-1">
                                        <CheckCircleIcon className="w-3.5 h-3.5" /> Le password coincidono
                                    </p>
                                )}
                                {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                            </div>

                            <button
                                type="submit"
                                disabled={loading || !PASSWORD_RULES.every(r => r.test(formData.password))}
                                className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Creazione account...
                                    </div>
                                ) : 'Completa registrazione →'}
                            </button>
                        </form>
                    </div>
                </div>

                <p className="text-center text-xs text-gray-400 mt-6">
                    🔒 La tua password è crittografata e al sicuro
                </p>
            </div>
        </div>
    );
};

export default AcceptInvitePage;